import { z } from "zod";

import { assertNever } from "../errors";

export const McpClientToolName = z.string().brand("McpClientToolName");
export type McpClientToolName = z.infer<typeof McpClientToolName>;

const McpClientToolNameSchema = McpClientToolName;

const McpServerToolAnnotations = z.object({
  destructiveHint: z.boolean().optional(),
  idempotentHint: z.boolean().optional(),
  openWorldHint: z.boolean().optional(),
  readOnlyHint: z.boolean().optional(),
  title: z.string().optional(),
});
export type McpServerToolAnnotations = z.infer<typeof McpServerToolAnnotations>;
const McpServerToolMetadata = z.object({
  annotations: McpServerToolAnnotations.optional(),
  description: z.string().optional(),
});
export const McpServerTools = z.record(
  McpClientToolNameSchema,
  McpServerToolMetadata,
);
export type McpServerTools = z.infer<typeof McpServerTools>;

const McpServerToolConfigEntry = z.object({ enabled: z.boolean() });
export const McpServerToolConfig = z.record(
  McpClientToolNameSchema,
  McpServerToolConfigEntry,
);
export type McpServerToolConfig = z.infer<typeof McpServerToolConfig>;

/**
 * Policy applied to tools not present in toolConfig.
 */
export const McpServerUnseenToolPolicy = z.enum([
  "ALLOW_UNSEEN_TOOLS",
  "DENY_UNSEEN_TOOLS",
  "ONLY_ALLOW_UNSEEN_READONLY_TOOLS",
]);
export type McpServerUnseenToolPolicy = z.infer<
  typeof McpServerUnseenToolPolicy
>;

export function enabledForDefaultPolicy(
  policy: McpServerUnseenToolPolicy,
  annotations: McpServerToolAnnotations | undefined,
): boolean {
  switch (policy) {
    case McpServerUnseenToolPolicy.Enum.ALLOW_UNSEEN_TOOLS:
      return true;
    case McpServerUnseenToolPolicy.Enum.DENY_UNSEEN_TOOLS:
      return false;
    case McpServerUnseenToolPolicy.Enum.ONLY_ALLOW_UNSEEN_READONLY_TOOLS:
      return annotations?.readOnlyHint ?? false;
    default:
      assertNever(policy, policy);
  }
}

// ---------------------------------------------------------------------------
// User-level tool config, layered on top of the org config above.
// ---------------------------------------------------------------------------

/**
 * Explicit per-tool action a user can set for a tool.
 */
export const UserMcpServerToolPolicy = z.enum([
  "ALWAYS_ALLOW",
  "ALWAYS_ASK",
  "DENY",
]);
export type UserMcpServerToolPolicy = z.infer<typeof UserMcpServerToolPolicy>;

export const UserMcpServerToolConfig = z.record(
  McpClientToolNameSchema,
  UserMcpServerToolPolicy,
);
export type UserMcpServerToolConfig = z.infer<typeof UserMcpServerToolConfig>;

/**
 * Policy applied to tools not present in the user's userToolConfig. Unlike the
 * per-tool policy, the default can be ASK_WRITE: read-only tools are
 * auto-allowed and tools not explicitly marked read-only require approval.
 */
export const UserMcpServerUnseenToolPolicy = z.enum([
  "ALWAYS_ALLOW",
  "ALWAYS_ASK",
  "ASK_WRITE",
]);
export type UserMcpServerUnseenToolPolicy = z.infer<
  typeof UserMcpServerUnseenToolPolicy
>;

/**
 * The approval action the agent should take for a user-level MCP tool call.
 * `allow` skips the confirm prompt, `ask` triggers HITL, `deny` rejects.
 */
export type UserMcpToolAction = "allow" | "ask" | "deny";

/**
 * The per-tool policy a tool resolves to under a default (unseen) policy when
 * the user has not set an explicit choice for it. ASK_WRITE auto-allows
 * read-only tools and asks for everything else — including tools with unknown
 * annotations, so the gate fails safe. The default policy never resolves to
 * DENY, which the return type encodes.
 *
 * This is the single source of truth for default-policy semantics shared by the
 * server enforcement (`resolveUserToolAction`) and the settings UI's effective
 * policy display.
 */
export function effectiveUserToolPolicyForDefault(
  defaultUserToolPolicy: UserMcpServerUnseenToolPolicy,
  annotations: McpServerToolAnnotations | undefined,
): Exclude<UserMcpServerToolPolicy, typeof UserMcpServerToolPolicy.Enum.DENY> {
  switch (defaultUserToolPolicy) {
    case UserMcpServerUnseenToolPolicy.Enum.ALWAYS_ALLOW:
      return UserMcpServerToolPolicy.Enum.ALWAYS_ALLOW;
    case UserMcpServerUnseenToolPolicy.Enum.ALWAYS_ASK:
      return UserMcpServerToolPolicy.Enum.ALWAYS_ASK;
    case UserMcpServerUnseenToolPolicy.Enum.ASK_WRITE:
      return annotations?.readOnlyHint === true
        ? UserMcpServerToolPolicy.Enum.ALWAYS_ALLOW
        : UserMcpServerToolPolicy.Enum.ALWAYS_ASK;
    default:
      return assertNever(defaultUserToolPolicy, defaultUserToolPolicy);
  }
}

/**
 * Resolve the approval action for a tool call against a user's connection
 * config. An explicit per-tool entry always wins; otherwise the default
 * policy applies. For ASK_WRITE, tools with `readOnlyHint: true` are allowed
 * and everything else — including tools with unknown annotations — is asked,
 * so the gate fails safe.
 */
export function resolveUserToolAction({
  annotations,
  defaultUserToolPolicy,
  toolName,
  userToolConfig,
}: {
  annotations: McpServerToolAnnotations | undefined;
  defaultUserToolPolicy: UserMcpServerUnseenToolPolicy;
  toolName: string;
  userToolConfig: UserMcpServerToolConfig | null;
}): UserMcpToolAction {
  const toolKey = McpClientToolName.parse(toolName);
  const parsedDefaultUserToolPolicy = UserMcpServerUnseenToolPolicy.parse(
    defaultUserToolPolicy,
  );
  const parsedUserToolConfig =
    UserMcpServerToolConfig.nullable().parse(userToolConfig);
  const explicit =
    parsedUserToolConfig != null && Object.hasOwn(parsedUserToolConfig, toolKey)
      ? parsedUserToolConfig[toolKey]
      : undefined;
  if (explicit != null) {
    switch (explicit) {
      case UserMcpServerToolPolicy.Enum.ALWAYS_ALLOW:
        return "allow";
      case UserMcpServerToolPolicy.Enum.ALWAYS_ASK:
        return "ask";
      case UserMcpServerToolPolicy.Enum.DENY:
        return "deny";
      default:
        assertNever(explicit, explicit);
    }
  }

  const effectivePolicy = effectiveUserToolPolicyForDefault(
    parsedDefaultUserToolPolicy,
    annotations,
  );
  switch (effectivePolicy) {
    case UserMcpServerToolPolicy.Enum.ALWAYS_ALLOW:
      return "allow";
    case UserMcpServerToolPolicy.Enum.ALWAYS_ASK:
      return "ask";
    default:
      return assertNever(effectivePolicy, effectivePolicy);
  }
}
